import type { JSX } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import type { ClientResource, QontoClientResource } from '@majuscule/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  BillingFields,
  billingFieldsSchema,
  type BillingFieldsValues,
  billingPayload,
  billingValuesOf,
} from './billing-fields';
import { QontoClientSearch } from './QontoClientSearch';
import { useToast } from './toast';
import { ApiHttpError, internalSend } from '../lib/api';
import { Callout } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHead } from '@/components/ui/card';
import { Dialog, DialogBody, DialogContent, DialogHead } from '@/components/ui/dialog';

/** The French `detail` of an API error, or the fallback message. */
function errorMessage(error: unknown, fallback: string): string {
  return error instanceof ApiHttpError && error.detail !== null ? error.detail : fallback;
}

/**
 * Billing details of the client (lot 18). Qonto holds them (D-QONTO.8): an
 * edit here is written to Qonto, then copied back from Qonto, and « Synchroniser »
 * copies an edit made in Qonto. Without address or SIREN, the monthly close
 * cannot issue the invoice.
 */
export function ClientBillingInfo({ client }: { client: ClientResource }): JSX.Element {
  const qc = useQueryClient();
  const { notify } = useToast();
  const [linking, setLinking] = useState(false);
  const form = useForm<BillingFieldsValues>({
    resolver: zodResolver(billingFieldsSchema),
    defaultValues: billingValuesOf(client),
  });

  const refreshed = (updated: ClientResource, message: string): void => {
    form.reset(billingValuesOf(updated));
    void qc.invalidateQueries({ queryKey: ['clients'] });
    notify(message);
  };

  const save = useMutation({
    mutationFn: (values: BillingFieldsValues) =>
      internalSend<ClientResource>('PATCH', `/internal/clients/${client.id}`, billingPayload(values)),
    onSuccess: (updated) => {
      refreshed(updated, 'Informations de facturation enregistrées dans Qonto');
    },
  });
  const sync = useMutation({
    mutationFn: () => internalSend<ClientResource>('POST', `/internal/clients/${client.id}/qonto/sync`),
    onSuccess: (updated) => {
      refreshed(updated, 'Fiche synchronisée depuis Qonto');
    },
  });
  const link = useMutation({
    mutationFn: (record: QontoClientResource) =>
      internalSend<ClientResource>('POST', `/internal/clients/${client.id}/qonto/link`, {
        qontoClientId: record.id,
      }),
    onSuccess: (updated) => {
      setLinking(false);
      refreshed(updated, 'Client lié à Qonto');
    },
  });

  const linked = client.qontoClientId !== null;
  return (
    <Card>
      <CardHead
        title="Informations de facturation"
        description="Fiche du client dans Qonto, qui émet la facture."
        actions={
          <>
            <Badge variant={linked ? 'success' : 'warning'}>{linked ? 'Lié' : 'Non lié'}</Badge>
            {linked ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={sync.isPending}
                onClick={() => {
                  sync.mutate();
                }}
              >
                Synchroniser depuis Qonto
              </Button>
            ) : null}
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                link.reset();
                setLinking(true);
              }}
            >
              {linked ? 'Changer de client Qonto' : 'Lier à un client Qonto'}
            </Button>
          </>
        }
      />
      <form
        onSubmit={(event) => {
          void form.handleSubmit((values) => {
            save.mutate(values);
          })(event);
        }}
      >
        <CardContent className="grid gap-4">
          {!linked ? (
            <Callout variant="warning">
              Client non lié à Qonto : le lier à sa fiche Qonto, ou enregistrer ce formulaire pour
              la créer (la clôture le fera sinon à partir du SIREN).
            </Callout>
          ) : null}
          <BillingFields form={form} />
          {save.isError ? (
            <Callout variant="destructive">
              {errorMessage(save.error, 'Échec de l’enregistrement.')}
            </Callout>
          ) : null}
          {sync.isError ? (
            <Callout variant="destructive">
              {errorMessage(sync.error, 'Échec de la synchronisation.')}
            </Callout>
          ) : null}
        </CardContent>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-3">
          <p className="m-0 max-w-prose text-caption text-muted-foreground">
            Enregistrer modifie la fiche dans Qonto. Le SIREN sert aussi d’adresse de facturation
            électronique : si le client est inscrit à l’annuaire, Qonto lui envoie la facture par le
            réseau de facturation électronique, sinon par email.
          </p>
          <Button type="submit" disabled={save.isPending}>
            Enregistrer
          </Button>
        </div>
      </form>

      <Dialog open={linking} onOpenChange={setLinking}>
        <DialogContent>
          <DialogHead
            title="Lier à un client Qonto"
            description="La fiche de facturation sera reprise de Qonto."
          />
          <DialogBody>
            <QontoClientSearch
              currentClientId={client.id}
              onSelect={(record) => {
                link.mutate(record);
              }}
            />
            {link.isError ? (
              <Callout variant="destructive">{errorMessage(link.error, 'Échec de la liaison.')}</Callout>
            ) : null}
          </DialogBody>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
